import React, { useContext } from "react";
import ImageContainer from "@components/imageContainer";
import { resolvePath } from "@utils/resolvePath";
import DOMPurify from "dompurify";
import Modal from "@components/modal";
import { Context } from "@components/useContext/context";
import { getWidgetSpecificIsVisible } from "@utils/index";
import { CommonProps } from "@utils/interface";
import { connect } from "react-redux";
import {
  ZdTable,
  Row,
  ZdItem,
  ZdExampleCard,
  RecommendedTag,
  RecommendedText,
  ExampleDescription,
  ModalContentWrapper,
  DescriptionWrapper,
  TableContainer,
  TableWrapper,
  ExampleTitle,
  RepairCostBox,
  RepairCostTitle,
  DepreciationNote,
  ComparisonRow,
  ComparisonRowWithBorder,
  IconWrapper,
  ComparisonContent,
  ComparisonTitle,
  ComparisonDescription
} from "@components/zeroDepModal/styles";

// Kept zero dep data here instead of in config because firefly config makes the second 
// column first and first column second, which breaks the column order
const ZERO_DEP_DATA: IZeroDepDataItem[] = [
  {
    feature_table_title: "Depreciation % <br />  on various parts",
    with_zd_table_title: "With <br /> Zero Dep.",
    without_zd_table_title: "Without <br /> Zero Dep."
  },
  {
    feature: "For all plastic, rubber, nylon parts and batteries",
    with: "0%",
    without: "50%"
  },
  {
    feature: "For fibre components",
    with: "0%",
    without: "30%"
  },
  {
    feature: "For glass components",
    with: "0%",
    without: "0%"
  },
  {
    feature: "For metal, wooden and all other parts",
    with: "0%",
    without: "25%"
  },
  {
    feature: "For paint",
    with: "0%",
    without: "50%"
  }
];

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    )
  };
};

export interface IZeroDepDataItem {
  feature_table_title?: string;
  with_zd_table_title?: string;
  without_zd_table_title?: string;
  feature?: string;
  with?: string;
  without?: string;
}

export interface IZDModal extends CommonProps {
  modal_heading?: string;
  description?: string;
  example_title?: string;
  example_description?: string;
  repair_cost?: string;
  depreciation_note?: string;
  with_zd_title?: string;
  with_zd_description?: string;
  without_zd_title?: string;
  without_zd_description?: string;
  zero_dep_data?: IZeroDepDataItem[];
}

const ZDModal = (props: IZDModal) => {
  const {
    is_visible,
    modal_heading,
    description,
    example_title,
    example_description,
    repair_cost,
    depreciation_note,
    with_zd_title,
    with_zd_description,
    without_zd_title,
    without_zd_description,
    zero_dep_data,
    actions
  } = props;
  const { isDesktop } = useContext(Context);
  const { on_close_modal } = actions || {};
  const zeroDepData = zero_dep_data || ZERO_DEP_DATA;
  return (
    <Modal
      showModal={is_visible}
      onClose={() => {
        on_close_modal?.forEach((eachFunc: any) => {
          typeof eachFunc.funCall === "function" &&
            eachFunc.funCall(eachFunc.args);
        });
      }}
      modalHeading={modal_heading}
      modalHeadingStyles={{
        color: "rgb(3, 3, 3)",
        fontSize: "1.25rem",
        ...(!isDesktop && { maxWidth: "80%" }),
        padding: isDesktop ? "2rem 2rem 0px" : "2rem 1.25rem 0px"
      }}
      showModalFooter={true}
      showPrimaryCta={true}
      primaryCtaText="Okay"
      primaryCtaClickHandler={() => {
        on_close_modal?.forEach((eachFunc: any) => {
          typeof eachFunc.funCall === "function" &&
            eachFunc.funCall(eachFunc.args);
        });
      }}
      wrapperStyles={{ padding: "0rem" }}
      styles={{ padding: "0rem" }}
      footerWrapperStyles={{ padding: "1.25rem" }}
      footerWrapperDStyles={{ padding: "2rem" }}
      closeButtonStyles={{
        ...(!isDesktop && { right: "1.25rem" }),
        top: "2rem",
        right: isDesktop ? "2rem" : "1.25rem"
      }}
    >
      <ModalContentWrapper isDesktop={isDesktop}>
        <DescriptionWrapper
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(description || "")
          }}
        />

        <TableContainer>
          <TableWrapper>
            <RecommendedTag>
              <RecommendedText>Recommended</RecommendedText>
            </RecommendedTag>
            <ZdTable>
              {zeroDepData.map((zdItem, itemIdx) => {
                return (
                  <Row key={itemIdx}>
                    {Object.values(zdItem).map((value, idx) => (
                      <ZdItem
                        key={idx}
                        isTitle={itemIdx === 0}
                        isFeature={idx === 0}
                        isSecondColumn={idx === 1}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(String(value))
                        }}
                      />
                    ))}
                  </Row>
                );
              })}
            </ZdTable>
          </TableWrapper>
        </TableContainer>

        <ExampleTitle>{example_title}</ExampleTitle>

        <ExampleDescription>{example_description}</ExampleDescription>

        <ZdExampleCard>
          <RepairCostBox>
            <RepairCostTitle>Repair cost: {repair_cost}</RepairCostTitle>
            <DepreciationNote>
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(depreciation_note || "")
                }}
              />
            </DepreciationNote>
          </RepairCostBox>

          <ComparisonRowWithBorder flexDirection="row">
            <IconWrapper>
              <ImageContainer
                imgSrc={resolvePath("/images/icons/green-tick.svg")}
                width={22}
                height={22}
                alt={"tick"}
              />
            </IconWrapper>
            <ComparisonContent>
              <ComparisonTitle>{with_zd_title}</ComparisonTitle>
              <ComparisonDescription>
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(with_zd_description || "")
                  }}
                />
              </ComparisonDescription>
            </ComparisonContent>
          </ComparisonRowWithBorder>

          <ComparisonRow flexDirection="row">
            <IconWrapper>
              <ImageContainer
                imgSrc={resolvePath("/images/icons/warning.svg")}
                width={22}
                height={22}
                alt={"tick"}
              />
            </IconWrapper>
            <ComparisonContent>
              <ComparisonTitle>{without_zd_title}</ComparisonTitle>
              <ComparisonDescription>
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(without_zd_description || "")
                  }}
                />
              </ComparisonDescription>
            </ComparisonContent>
          </ComparisonRow>
        </ZdExampleCard>
      </ModalContentWrapper>
    </Modal>
  );
};

export default connect(mapStateToProps)(ZDModal);
